import { useState, useEffect } from 'preact/hooks'
import { parseSizeString, getAvailableGenders, getAvailableSizeTypes } from './SizeFilterUtils'

export const UniversalSizeFilter = ({ values, onFilter }) => {
  const [activeGender, setActiveGender] = useState('')
  const [activeSizeType, setActiveSizeType] = useState('')

  const parsedSizes = parseSizeData(values)
  const { universalSizes, nonUniversalSizes } = parsedSizes

  const availableGenders = getAvailableGenders(universalSizes)
  const availableSizeTypes = getAvailableSizeTypes(universalSizes)

  useEffect(() => {
    if (availableGenders.length > 0) {
      if (!activeGender || !availableGenders.includes(activeGender)) {
        setActiveGender(availableGenders[0])
      }
    }
  }, [availableGenders, activeGender])

  useEffect(() => {
    const preferredSizeRange = localStorage.getItem('preferred_size_range') || 'US'
    if (availableSizeTypes.includes(preferredSizeRange)) {
      setActiveSizeType(preferredSizeRange)
    } else if (availableSizeTypes.length > 0) {
      setActiveSizeType(availableSizeTypes[0])
    }
  }, [availableSizeTypes])

  const handleSizeTypeClick = (sizeType) => {
    setActiveSizeType(sizeType)
    localStorage.setItem('preferred_size_range', sizeType)
  }

  const handleGenderClick = (gender) => {
    setActiveGender(gender)
  }

  return (
    <div className="flex flex-col gap-y-1">
      {nonUniversalSizes.length > 0 && (
        <div className="flex w-full flex-wrap items-center gap-0.5">
          {nonUniversalSizes.map((item) => (
            <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
          ))}
        </div>
      )}

      {Object.keys(universalSizes).length > 0 && (
        <div className="flex flex-col gap-y-1">
          {availableGenders.length > 0 && (
            <div className="flex w-full items-center bg-neutral-light p-0.25 text-center">
              {availableGenders.map((gender) => (
                <div
                  key={gender}
                  className={`items center flex min-h-[36px] w-full justify-center p-0.625 link-fade ${
                    activeGender === gender ? 'bg-brand-white' : ''
                  }`}
                  onClick={() => handleGenderClick(gender)}
                >
                  <a className="flex text-s font-bold">
                    {gender === 'WOMENS' ? 'Womens' : gender === 'MENS' ? 'Mens' : gender.toLowerCase()}
                  </a>
                </div>
              ))}
            </div>
          )}

          {availableSizeTypes.length > 0 && (
            <div className="flex w-full items-center bg-neutral-light p-0.25 text-center">
              {availableSizeTypes.map((sizeType) => (
                <div
                  key={sizeType}
                  className={`items center flex min-h-[36px] w-full justify-center p-0.625 link-fade ${
                    activeSizeType === sizeType ? 'bg-brand-white' : ''
                  }`}
                  onClick={() => handleSizeTypeClick(sizeType)}
                >
                  <a className="flex text-s font-bold">{sizeType === 'EU' ? 'EU' : sizeType}</a>
                </div>
              ))}
            </div>
          )}

          <div className="flex w-full flex-wrap items-center gap-0.5">
            {universalSizes[activeGender]?.[activeSizeType]?.map((item) => (
              <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SizeGridItem = ({ item, onFilter }) => {
  return (
    <div
      className={`
        w-full max-w-grid-1/5 btn btn-border btn-primary btn-primary-alt btn-m !px-0.25
        ${item.checked ? 'active' : ''}
      `}
      onClick={() => onFilter(item.value)}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={() => onFilter(item.value)}
        className="sr-only"
        aria-label={item.displayLabel || item.label}
      />
      <span class="overflow-hidden text-ellipsis whitespace-nowrap">{item.size || item.label}</span>
    </div>
  )
}

const parseSizeData = (values) => {
  const universalSizes = {}
  const nonUniversalSizes = []
  const sizeValueMap = new Map()

  values.forEach((item) => {
    if (item.label?.includes(',')) {
      const sizesByGender = parseSizeString(item.label)
      if (sizesByGender) {
        Object.entries(sizesByGender).forEach(([gender, sizesByType]) => {
          Object.entries(sizesByType).forEach(([sizeType, sizeValue]) => {
            const sizeKey = `${sizeType}-${sizeValue}`

            if (!sizeValueMap.has(sizeKey)) {
              sizeValueMap.set(sizeKey, {
                sizeType,
                sizeValue,
                genders: new Set(),
                item
              })
            }

            sizeValueMap.get(sizeKey).genders.add(gender)
          })
        })
      }
    } else {
      nonUniversalSizes.push(item)
    }
  })

  sizeValueMap.forEach(({ sizeType, sizeValue, genders, item }) => {
    const genderList = Array.from(genders)

    genderList.forEach((gender) => {
      if (!universalSizes[gender]) {
        universalSizes[gender] = {}
      }
      if (!universalSizes[gender][sizeType]) {
        universalSizes[gender][sizeType] = []
      }

      universalSizes[gender][sizeType].push({
        ...item,
        size: sizeValue,
        sizeType,
        displayLabel: `${sizeType === 'EU' ? 'EU' : sizeType} ${sizeValue}`
      })
    })
  })

  Object.keys(universalSizes).forEach((gender) => {
    Object.keys(universalSizes[gender]).forEach((sizeType) => {
      universalSizes[gender][sizeType].sort((a, b) => {
        return parseFloat(a.size) - parseFloat(b.size)
      })
    })
  })

  return { universalSizes, nonUniversalSizes }
}
